import algoliasearch from "algoliasearch/lite";
import { NextSeo } from "next-seo";
import { useState, useContext, useEffect } from "react";
import { renderToString } from "react-dom/server";
import {
  Configure,
  Hits,
  InstantSearch,
  InstantSearchSSRProvider,
  useCurrentRefinements,
  usePagination,
  useSortBy
} from "react-instantsearch-hooks-web";
import { getServerState } from "react-instantsearch-hooks-server";

import { useNextRouterHandler, decodeParam } from "@/hooks/useNextRouterHandler";
import { useSpecials } from "@/context/specials";
import StoreContext from "@/context/store";
import { getSiteSettings } from '@/lib/getSiteSettings';

import Header from "@/components/shop/Header";
import ShopNav from "@/components/shop/ShopNav";
import ProductCard from "@/components/product/ProductCard";
import NoResults from "@/components/shop/NoResults";
import Stats from "@/components/shop/Stats";
import CurrentRefinements from "@/components/shop/CurrentRefinements";
import Pagination from "@/components/shop/Pagination";

import styles from "./shop.module.scss";
import productCardStyles from "@/components/product/ProductCard.module.scss";
import SpecialSlider from "@/components/blocks/sliders/specialSlider";

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_KEY
);

const indexName = "menu-products-by-rating-production";

// Serialize URL param from array to string
const serializeParam = (param, key) =>
  param?.length
    ? param
      .map((item) =>
        key === "brand" ? item : item.toLowerCase().replace(/ /g, "+")
      )
      .join(",")
    : null;

export async function getServerSideProps({ req }) {
  const siteSettings = await getSiteSettings();
  const protocol = req.headers.referer?.split("://")[0] || "https";
  const url = `${protocol}://${req.headers.host}${req.url}`;
  const initialStore = req.cookies.JANE_STORE || 4398;

  const serverState = await getServerState(
    <Shop url={url} initialStore={initialStore} />, { renderToString }
  );


  return {
    props: {
      serverState,
      url,
      initialStore,
      siteSettings
    },
  };
}

export default function Shop({ serverState, url, initialStore }) {

  const { store } = useContext(StoreContext);
  const { specials, fetchAllSpecials } = useSpecials();
  const [list, setList] = useState(false);

  const { initialUiState, NextRouterHandler } = useNextRouterHandler({
    url,
    routeToState: (params) => {
      return {
        [indexName]: {
          query: params.q || null,
          refinementList: {
            root_types: params.category ? decodeParam(params.category, "root_types") : null,
            brand: params.brand ? decodeParam(params.brand, "brand") : null,
            category: params.strain ? decodeParam(params.strain, "category") : null,
            feelings: params.effect ? decodeParam(params.effect, "feelings") : null,
            available_weights: params.weight ? decodeParam(params.weight, "available_weights") : null,
            applicable_special_ids: params.special ? decodeParam(params.special, "applicable_special_ids") : null,
          },
          range: {
            bucket_price: params.price || null,
            percent_thc: params.thc || null,
            percent_cbd: params.cbd || null,
          },
          page: params.page || null,
          sortBy: params.sortBy || null
        },
      };
    },
    stateToRoute: (uiState) => {
      const indexUiState = uiState[indexName];
      return {
        category: indexUiState.refinementList ? serializeParam(indexUiState.refinementList?.root_types) : null,
        brand: serializeParam(indexUiState.refinementList?.brand, "brand"),
        strain: serializeParam(indexUiState.refinementList?.category),
        effect: serializeParam(indexUiState.refinementList?.feelings),
        weight: serializeParam(indexUiState.refinementList?.available_weights),
        special: serializeParam(indexUiState.refinementList?.applicable_special_ids),
        price: indexUiState.range?.bucket_price || null,
        thc: indexUiState.range?.percent_thc || null,
        cbd: indexUiState.range?.percent_cbd || null,
        q: indexUiState.query || null,
        page: indexUiState.page || null,
        sortBy: indexUiState.sortBy || null,
      };
    },
  });

  useEffect(() => {
    if (store) fetchAllSpecials(store);
  }, [store, fetchAllSpecials]);

  return (
    <>
      <NextSeo
        title="Shop All Cannabis Products | Purple Lotus"
        description="Shop cannabis flower, edibles, vapes, and extracts at Purple Lotus — the #1 dispensary in San Jose. Enjoy fast pickup and delivery across the Bay Area."
        canonical="https://plpcsanjose.com/shop"
        openGraph={{
          url: "https://plpcsanjose.com/shop",
          title: "Shop All Cannabis Products | Purple Lotus",
          description:
            "Shop cannabis flower, edibles, vapes, and extracts at Purple Lotus — the #1 dispensary in San Jose. Enjoy fast pickup and delivery across the Bay Area.",
          images: [
            {
              url: "https://plpcsanjose.com/images/purple-lotus-store-counters.jpg",
              width: 800,
              height: 600,
              alt: "Purple Lotus Cannabis Dispensary",
              type: "image/jpeg",
            },
          ],
          siteName: "Purple Lotus",
        }}
      />


      <main className="container container__slim">
        <InstantSearchSSRProvider {...serverState}>
          <InstantSearch
            searchClient={searchClient}
            indexName={indexName}
            initialUiState={initialUiState}
          >
            <NextRouterHandler />
            <Configure filters={`store_id:${store || initialStore}`} />

            <div className={styles.header}>
              <Header category="all" url={url} />
            </div>

            <ShopNav url={url} list={list} setList={setList} />

            <div className={styles.stats}>
              <Stats />
              <CurrentRefinements productSpecials={specials} />
            </div>

            <ShowSpecialsSlider list={list} />

            <div className={list ? productCardStyles.list : "shop-product-grid"}>
              <NoResults>
                <Hits hitComponent={ProductCard} />
                <Pagination />
              </NoResults>
            </div>

          </InstantSearch>
        </InstantSearchSSRProvider>
      </main>
    </>
  );
}



function ShowSpecialsSlider({ list }) {
  const { items } = useCurrentRefinements();
  const { currentRefinement: currentPage } = usePagination();

  const hasRefinements = items.length > 0;
  const isFirstPage = currentPage === 0;

  if (hasRefinements || !isFirstPage || list) return null;

  return <SpecialSlider title="Deals" viewAllLink="/deals" />;
}
